import axios from 'axios'

const LEVELS_PATH = '/foodcourt/levels'
const DAYS_PATH = '/foodcourt/levels/days'
const CLAIMS_PATH = '/foodcourt/levels/claims'
const CLAIMS_MISMATCH = 'CLAIMS_MISMATCH'

const METRIC_COLUMN = {
  incidents: 'incident_ratio',
  availability: 'availability_pct',
  active_menu: 'active_menu_pct',
  rating: 'rating_average'
}

const IGNORED_MISSING_METRICS = ['rating']

const isClosed = period => period.is_closed === true
const hasLevel = period => period.level !== null && period.level !== undefined

const missingMetricsOf = day => (Array.isArray(day.missing_metrics) ? day.missing_metrics : [])

const hasMeasured = (day, metric) => !missingMetricsOf(day).includes(metric)

const isComparable = day =>
  day.score !== null &&
  day.score !== undefined &&
  missingMetricsOf(day).every(metric => IGNORED_MISSING_METRICS.includes(metric))

const toLocalIsoDate = date => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${date.getFullYear()}-${month}-${day}`
}

const FORTNIGHT_RANGE = 'fortnight'
const LAST_MONTH_RANGE = 'last_month'
const RANGE_DAYS = {
  [FORTNIGHT_RANGE]: 14,
  [LAST_MONTH_RANGE]: 30
}

export { FORTNIGHT_RANGE, LAST_MONTH_RANGE }

const state = {
  periods: null,
  thresholds: [],
  cutoffs: [],
  pagination: { page: 1, lastPage: 1, total: 0, perPage: 10 },
  claims: null,
  claimsStatus: null,
  days: null,
  daysRange: { from: null, to: null },
  selectedDaysRange: FORTNIGHT_RANGE
}

const getters = {
  getPeriods: state => state.periods,
  getPagination: state => state.pagination,
  getClosedPeriods: state => (state.periods || []).filter(isClosed),
  getLastClosedRatedPeriod: (state, getters) =>
    getters.getClosedPeriods.find(hasLevel) || null,
  getThresholds: state => state.thresholds,
  getThreshold: state => metric => state.thresholds.find(t => t.metric === metric) || null,
  getCutoffs: state => state.cutoffs,
  getClaims: state => state.claims,
  claimsMismatch: state => state.claimsStatus === CLAIMS_MISMATCH,

  getDays: state => state.days,
  getDaysRange: state => state.daysRange,
  isLoadingDays: state => state.days === null,
  hasNoDays: state => state.days !== null && state.days.length === 0,
  getDayLabels: state => (state.days || []).map(day => day.level_date),
  getSelectedDaysRange: state => state.selectedDaysRange,

  getMetricSeries: state => metric => {
    const column = METRIC_COLUMN[metric]
    const days = state.days || []

    return {
      values: days.map(day => (hasMeasured(day, metric) ? Number(day[column]) : null)),
      measured: days.filter(day => hasMeasured(day, metric)).length,
      total: days.length,
      unmeasuredDates: days.filter(day => !hasMeasured(day, metric)).map(day => day.level_date)
    }
  },

  getLevelSeries: state => {
    const days = state.days || []

    return {
      values: days.map(day => (isComparable(day) ? Number(day.score) : null))
    }
  },

  getActivitySummary: state => {
    const days = state.days || []

    return {
      orders: days.reduce((total, day) => total + Number(day.orders_count || 0), 0),
      claims: days.reduce((total, day) => total + Number(day.claims_count || 0), 0)
    }
  }
}

const mutations = {
  SET_LEVELS (state, result) {
    state.periods = result.periods
    state.thresholds = result.thresholds
    state.cutoffs = result.cutoffs
    state.pagination = {
      page: result.page,
      lastPage: result.lastPage,
      total: result.total,
      perPage: result.perPage
    }
  },
  SET_CLAIMS (state, result) {
    state.claims = result
    state.claimsStatus = result && result.matches_engine === false ? CLAIMS_MISMATCH : null
  },
  CLEAR_CLAIMS (state) {
    state.claims = null
    state.claimsStatus = null
  },
  SET_DAYS (state, result) {
    state.days = result.days
    state.daysRange = { from: result.from, to: result.to }
  },
  SET_SELECTED_DAYS_RANGE (state, range) {
    state.selectedDaysRange = range
  }
}

const actions = {
  async GET_LEVELS ({ commit }, page = 1) {
    const { data } = await axios.get(LEVELS_PATH, { params: { page } })
    commit('SET_LEVELS', data.payload.result)
  },
  async GET_CLAIMS ({ commit }, { start, end }) {
    const { data } = await axios.get(CLAIMS_PATH, { params: { start, end } })
    commit('SET_CLAIMS', data.payload.result)
  },
  async GET_DAYS ({ commit }, { from, to } = {}) {
    const params = {}
    if (from) params.from = from
    if (to) params.to = to

    const { data } = await axios.get(DAYS_PATH, { params })
    commit('SET_DAYS', data.payload.result)
  },

  async SELECT_DAYS_RANGE ({ commit, dispatch }, range) {
    commit('SET_SELECTED_DAYS_RANGE', range)

    const days = RANGE_DAYS[range] || RANGE_DAYS[FORTNIGHT_RANGE]
    const to = new Date()
    const from = new Date()
    from.setDate(to.getDate() - (days - 1))

    await dispatch('GET_DAYS', {
      from: toLocalIsoDate(from),
      to: toLocalIsoDate(to)
    })
  }
}

export default { namespaced: true, state, getters, mutations, actions }
